import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDecimal,
} from '@/components/_common';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import { useAffectPrice } from '@/core/domain/stock/StockItems/StockItems.hooks';
import {
  AffectPriceAffect,
  AffectPriceFormValues,
  AffectPriceSetPartPriceMethod,
  AffectPriceType,
  SetPartPriceOption,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Col, Form, Modal, ModalProps, Radio, Row, Select } from 'antd';
import React from 'react';

interface Props extends ModalProps {
  onCancel: VoidFunction;
}

const PartsChangePricesModal: React.FC<Props> = ({ onCancel, ...props }) => {
  const [form] = Form.useForm<AffectPriceFormValues>();

  const affectPrice = useAffectPrice();

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleOk = async () => {
    const values = await form.validateFields();

    await affectPrice.mutateAsync(values);

    handleCancel();
  };

  const requiredField = useDefaultEmptyFieldError();

  return (
    <Modal
      title="Alterações de Preços"
      onCancel={handleCancel}
      onOk={handleOk}
      width={800}
      okText="Alterar"
      confirmLoading={affectPrice.isLoading}
      {...props}
    >
      <Form form={form}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <FloatingFormItem
              name="setPartPriceMethod"
              label="Selecione uma opção"
              initialValue={AffectPriceSetPartPriceMethod.allStock}
            >
              <Select
                options={[
                  {
                    label: 'Todo o Estoque',
                    value: AffectPriceSetPartPriceMethod.allStock,
                  },
                  {
                    label: 'Por Grupo',
                    value: AffectPriceSetPartPriceMethod.byGroup,
                  },
                  {
                    label: 'Por Fabricante',
                    value: AffectPriceSetPartPriceMethod.byManufacturer,
                  },
                ]}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const setPartPriceMethod = getFieldValue('setPartPriceMethod');
                const type = getFieldValue('type');

                return (
                  <Row gutter={[12, 12]}>
                    <Col
                      span={24}
                      md={
                        setPartPriceMethod ===
                        AffectPriceSetPartPriceMethod.allStock
                          ? 24
                          : 12
                      }
                    >
                      <FloatingFormItem
                        name="companyIds"
                        label="Empresas"
                        rules={[requiredField]}
                        style={{ margin: 0 }}
                      >
                        <CompaniesInfiniteSelectField mode="multiple" />
                      </FloatingFormItem>
                    </Col>

                    {setPartPriceMethod ===
                      AffectPriceSetPartPriceMethod.byGroup && (
                      <Col span={12}>
                        <FloatingFormItem
                          name="groupIds"
                          label="Grupos"
                          rules={[requiredField]}
                        >
                          <GroupsInfiniteSelectField mode="multiple" />
                        </FloatingFormItem>
                      </Col>
                    )}

                    {setPartPriceMethod ===
                      AffectPriceSetPartPriceMethod.byManufacturer && (
                      <Col span={12}>
                        <FloatingFormItem
                          name="manufacturerIds"
                          label="Selecione um fabricante"
                          rules={[requiredField]}
                        >
                          <ManufacturerInfiniteSelectField mode="multiple" />
                        </FloatingFormItem>
                      </Col>
                    )}

                    <Col span={24}>
                      <Form.Item
                        name="type"
                        noStyle
                        initialValue={AffectPriceType.byCostPrice}
                      >
                        <Radio.Group
                          optionType="button"
                          buttonStyle="solid"
                          size="large"
                        >
                          <Radio value={AffectPriceType.byCostPrice}>
                            Preço de custo
                          </Radio>
                          <Radio value={AffectPriceType.bySalePrice}>
                            Preço de Venda
                          </Radio>
                          <Radio value={AffectPriceType.byProfitPercentage}>
                            Percentual de lucro
                          </Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>

                    {type === AffectPriceType.byCostPrice && (
                      <Col span={24}>
                        <Form.Item
                          name="affect"
                          initialValue={AffectPriceAffect.AffectSalesPrice}
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                          >
                            <Radio value={AffectPriceAffect.AffectSalesPrice}>
                              Incluir sobre o preço de venda
                            </Radio>
                            <Radio
                              value={
                                AffectPriceAffect.AffectPercentageProfitMargin
                              }
                            >
                              Incidir sobre a margem percentual de lucro
                            </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    )}

                    {type === AffectPriceType.byProfitPercentage && (
                      <Col span={24}>
                        <Form.Item
                          name="option"
                          initialValue={SetPartPriceOption.Adjust}
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            size="large"
                          >
                            <Radio value={SetPartPriceOption.Adjust}>
                              Ajustar preço
                            </Radio>
                            <Radio value={SetPartPriceOption.Change}>
                              Alterar preço
                            </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                );
              }}
            </Form.Item>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="percentageIncidence"
              label="Percentual de incidência"
              initialValue={0}
              rules={[requiredField]}
            >
              <InputDecimal max={900} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default PartsChangePricesModal;
